/**
 * rendi-result-panel — the wizard's contact-data (details) screen.
 *
 * Owns the consent snippet (first name + email + phone + privacy consent) and
 * the inline submit status. Like the other wizard components it is dumb: the host
 * form drives it through setters and reads its inputs through
 * `rendi:field-change` events, while validation, the resolved estimate and the
 * submit itself all stay in the host. The name, email and consent fields can be
 * absent for logged-in customers (the snippet hides them), so every accessor
 * guards for the missing node.
 */
if (!customElements.get('rendi-result-panel')) {
  // Written only on change, so an error (role="alert") is not re-announced.
  const setText = (node, text) => {
    if (node && node.textContent !== text) node.textContent = text;
  };

  class RendiResultPanel extends HTMLElement {
    constructor() {
      super();
      this.handleFirstNameBound = this.handleFirstName.bind(this);
      this.handleEmailBound = this.handleEmail.bind(this);
      this.handlePhoneBound = this.handlePhone.bind(this);
      this.handleConsentBound = this.handleConsent.bind(this);
    }

    connectedCallback() {
      this.firstNameInput = this.querySelector('[data-first-name-input]');
      this.firstNameError = this.querySelector('[data-first-name-error]');
      this.emailInput = this.querySelector('[data-email-input]');
      this.emailError = this.querySelector('[data-email-error]');
      this.phoneInput = this.querySelector('[data-phone-input]');
      this.phoneError = this.querySelector('[data-phone-error]');
      this.phoneLabel = this.phoneInput && this.querySelector(`label[for="${this.phoneInput.id}"]`);
      this.consentCheckbox = this.querySelector('[data-consent-checkbox]');
      this.consentBox = this.consentCheckbox?.closest('.rendi-consent__box');
      this.consentError = this.querySelector('[data-consent-error]');
      this.submitStatus = this.querySelector('[data-submit-status]');

      this.firstNameInput?.addEventListener('input', this.handleFirstNameBound);
      this.emailInput?.addEventListener('input', this.handleEmailBound);
      this.phoneInput?.addEventListener('input', this.handlePhoneBound);
      this.consentCheckbox?.addEventListener('change', this.handleConsentBound);
    }

    disconnectedCallback() {
      this.firstNameInput?.removeEventListener('input', this.handleFirstNameBound);
      this.emailInput?.removeEventListener('input', this.handleEmailBound);
      this.phoneInput?.removeEventListener('input', this.handlePhoneBound);
      this.consentCheckbox?.removeEventListener('change', this.handleConsentBound);
    }

    /* ── Panel → host: keep the host's state in sync, same as the other fields ── */

    handleFirstName() {
      this.emit('firstName', this.firstNameInput.value.trim());
    }

    handleEmail() {
      this.emit('email', this.emailInput.value.trim());
    }

    handlePhone() {
      this.emit('phone', this.phoneInput.value.trim());
    }

    handleConsent() {
      this.emit('consent', this.consentCheckbox.checked);
    }

    emit(name, value) {
      this.dispatchEvent(new CustomEvent('rendi:field-change', {
        bubbles: true,
        detail: { name, value }
      }));
    }

    /* ── Field presence (name/email/consent can be hidden for logged-in customers) ── */

    hasFirstName() {
      return Boolean(this.firstNameInput);
    }

    hasEmail() {
      return Boolean(this.emailInput);
    }

    hasPhone() {
      return Boolean(this.phoneInput);
    }

    hasConsent() {
      return Boolean(this.consentCheckbox);
    }

    /* ── Host → panel ── */

    setValues({ firstName, email, phone, consent }) {
      if (this.firstNameInput && firstName !== undefined) {
        this.firstNameInput.value = firstName || '';
      }
      if (this.emailInput && email !== undefined) {
        this.emailInput.value = email || '';
      }
      if (this.phoneInput && phone !== undefined) {
        this.phoneInput.value = phone || '';
      }
      if (this.consentCheckbox && consent !== undefined) {
        this.consentCheckbox.checked = Boolean(consent);
      }
    }

    // The phone-required Experiment arm: the label text comes from Liquid.
    setPhoneRequired(required) {
      if (!this.phoneInput) return;
      this.phoneInput.required = required;
      setText(this.phoneLabel, required ? this.phoneLabel?.dataset.requiredText : this.phoneLabel?.dataset.optionalText);
    }

    // phone: 'missing' | 'invalid' | null picks the error text Liquid put on the node.
    showErrors({ firstName, email, phone, consent }) {
      if (this.firstNameInput) {
        this.firstNameInput.setAttribute('aria-invalid', firstName ? 'true' : 'false');
      }
      if (this.firstNameError) this.firstNameError.hidden = !firstName;
      if (this.emailInput) {
        this.emailInput.setAttribute('aria-invalid', email ? 'true' : 'false');
      }
      if (this.emailError) this.emailError.hidden = !email;
      if (this.phoneInput) {
        this.phoneInput.setAttribute('aria-invalid', phone ? 'true' : 'false');
        const helperId = this.phoneInput.id.replace('rendi-phone-', 'rendi-phone-helper-');
        this.phoneInput.setAttribute('aria-describedby',
          phone && this.phoneError ? `${helperId} ${this.phoneError.id}` : helperId);
      }
      if (this.phoneError) {
        this.phoneError.hidden = !phone;
        if (phone) setText(this.phoneError, phone === 'missing' ? this.phoneError.dataset.missingText : this.phoneError.dataset.invalidText);
      }
      if (this.consentBox) this.consentBox.classList.toggle('is-invalid', consent);
      if (this.consentError) this.consentError.hidden = !consent;
    }

    setStatus({ message, state }) {
      if (!this.submitStatus) return;
      this.submitStatus.textContent = message;
      this.submitStatus.hidden = state !== 'error' || !message;
      this.submitStatus.classList.toggle('is-error', state === 'error');
    }

    // Move focus to the field a failed submit flagged. `endpoint` has no field of
    // its own, so it falls back to the status area like the original did.
    focusField(field) {
      let target = null;
      if (field === 'firstName') target = this.firstNameInput || this.submitStatus;
      else if (field === 'email') target = this.emailInput || this.submitStatus;
      else if (field === 'phone') target = this.phoneInput || this.submitStatus;
      else if (field === 'consent') target = this.consentCheckbox;
      else target = this.submitStatus;

      if (target && typeof target.focus === 'function') {
        window.requestAnimationFrame(() => {
          target.focus({ preventScroll: true });
        });
      }
    }
  }

  customElements.define('rendi-result-panel', RendiResultPanel);
}
